// HU-04 (S16/S17/S18): which of the 4 Planned Jobs tabs are visible in the modal, and which list
// powers the widget's own main view, resolved from the per-device widget config persisted in
// widgets/device/{id} (data/setData, existing mechanism -- no new screen, no new permission, RN9).

export const TAB_KEYS = {
  ALL_PLANNED: 'allPlanned',
  COMPATIBLE: 'compatible',
  FINISHED_COMPATIBLE: 'finishedCompatible',
  // Single new tab (post-GATE-2 amendment), S9.
  ALL_ACTIVE: 'allActive',
  // Story 72 (S16, RN13): installation-wide planned/active rework jobs (NOK outputs),
  // tech-compatible with the querying device.
  REWORK: 'rework',
  // Story 100 (S106/S107, RN33, CA7): installation-wide, SOLELY VISIBILITY trips of an external
  // job of the circuit (RN1) -- fabrication order, job, technology, situation, promised return and
  // actual dates, with zero actions. Same opt-in mechanism as ALL_ACTIVE (#59), the only valid form
  // precedent for "installation-wide without a technology filter" (C12).
  EXTERNAL_CIRCUIT: 'externalCircuit',
  // Story 122 (S37/S54): the jobs that were FINISHED ON the querying device itself -- read from the
  // execution footprint, not from the plan -- as opposed to FINISHED_COMPATIBLE, which is
  // installation-wide and only filtered by technology compatibility. The two populations may
  // overlap and neither is a subset of the other (RN11/INV4), so nothing is deduplicated between
  // them.
  //
  // The key is deliberately NOT the `finishedPlanned` of the withdrawn delivery (S54/D11): that one
  // named a tab fed by the plan, answering a different question, and a work centre that had enabled
  // it must NOT find this one already switched on. Renaming the key is what drops that activation.
  FINISHED_IN_DEVICE: 'finishedInDevice',
  // Story 193 (MIC-4346, S24): the planned jobs of the technologies the configurator chose
  // (`plannedByTechnologyIds`), on ANY asset, finished included -- as opposed to ALL_PLANNED, which
  // only lists the jobs planned on this device's own asset. A new key, never `allPlanned` reused:
  // reusing it would switch the new tab on in every work centre that had the old one (RN2).
  PLANNED_BY_TECHNOLOGY: 'plannedByTechnology'
}

export const TAB_DEFINITIONS = [
  // Story 193: the widest list goes first, each tab to its right narrows it further.
  { key: TAB_KEYS.PLANNED_BY_TECHNOLOGY, label: 'All Jobs' },
  // Story 193 (S20/RN1): same key, same content, renamed to what it lists. "All Planned Jobs" stays
  // only as the dialog title.
  { key: TAB_KEYS.ALL_PLANNED, label: 'Planned Jobs of This Asset' },
  { key: TAB_KEYS.COMPATIBLE, label: 'Compatible Jobs' },
  { key: TAB_KEYS.FINISHED_COMPATIBLE, label: 'Finished Compatible Jobs' },
  { key: TAB_KEYS.ALL_ACTIVE, label: 'All Active Jobs' },
  { key: TAB_KEYS.REWORK, label: 'Reworks' },
  { key: TAB_KEYS.EXTERNAL_CIRCUIT, label: 'External jobs' },
  { key: TAB_KEYS.FINISHED_IN_DEVICE, label: 'Finished Jobs of This Asset' }
]

const KNOWN_TAB_KEYS = new Set(TAB_DEFINITIONS.map((tab) => tab.key))

// RN10/D5: without explicit config, only the 3 tabs that existed before this story are shown.
export const DEFAULT_VISIBLE_TABS = [TAB_KEYS.ALL_PLANNED, TAB_KEYS.COMPATIBLE, TAB_KEYS.FINISHED_COMPATIBLE]

export const PRIMARY_LIST_KEYS = {
  // The widget's own queue (compass/planned/device), NOT the "All Planned" tab. RN10/D5.
  DEFAULT: 'default',
  ...TAB_KEYS
}

export const DEFAULT_PRIMARY_LIST = PRIMARY_LIST_KEYS.DEFAULT

/**
 * Resolves which tabs are visible in the modal for a device's persisted config.
 * Falls back to the 3-tab retrocompatible default (RN10) when there is no config, it isn't an
 * array, or it ends up empty/unknown.
 */
export const getEffectiveVisibleTabs = (visibleTabs) => {
  if (!Array.isArray(visibleTabs)) return DEFAULT_VISIBLE_TABS
  const valid = visibleTabs.filter((key) => KNOWN_TAB_KEYS.has(key))
  return valid.length > 0 ? valid : DEFAULT_VISIBLE_TABS
}

/**
 * Whether a persisted list is (effectively) the retrocompatible 3-tab default, i.e. nobody has
 * taken a decision on it yet. Used to break the tie when two widgets seed the shared list from
 * their own config: a customised list wins over an untouched one.
 */
export const isDefaultVisibleTabs = (visibleTabs) => {
  const effective = getEffectiveVisibleTabs(visibleTabs)
  return effective.length === DEFAULT_VISIBLE_TABS.length &&
    DEFAULT_VISIBLE_TABS.every((key) => effective.includes(key))
}

/**
 * Resolves which list powers the widget's main view for a device's persisted config.
 * V3/E3: if the configured primary list points to a disabled or unknown tab, degrades silently to
 * the default (the widget's current queue), without breaking the grid.
 */
export const getEffectivePrimaryList = (primaryList, visibleTabs) => {
  if (primaryList === undefined || primaryList === null || primaryList === PRIMARY_LIST_KEYS.DEFAULT) {
    return PRIMARY_LIST_KEYS.DEFAULT
  }
  const effectiveVisibleTabs = getEffectiveVisibleTabs(visibleTabs)
  if (typeof primaryList === 'string' && effectiveVisibleTabs.includes(primaryList)) {
    return primaryList
  }
  return DEFAULT_PRIMARY_LIST
}

// Story 193 (RN5.1/RN5.2): shown instead of the "All Jobs" list -- in the dialog and in the main view
// -- when no chosen technology counts. It is also its i18n key.
export const NO_TECHNOLOGY_SELECTED_MESSAGE = 'Select at least one technology in the widget configuration to see its planned jobs'

const isTechnologyId = (value) => Number.isInteger(value) && value > 0

/**
 * Story 193 (S30/V1): reads the saved technology selection of the "All Jobs" tab. Only positive
 * integers count, without duplicates and in their saved order; anything that is not a list reads as
 * an empty selection. A strange value never rejects the rest of the widget config.
 */
export const getEffectivePlannedByTechnologyIds = (plannedByTechnologyIds) => {
  if (!Array.isArray(plannedByTechnologyIds)) return []
  return [...new Set(plannedByTechnologyIds.filter(isTechnologyId))]
}

/**
 * Story 193 (S32/S35/S36, RN4/P4): the technologies of the selection that still count, i.e. that are
 * in the loaded catalog of active technologies. When the catalog could not be loaded
 * (`catalogFailed`) the saved selection is used as-is: Sun discards the inactive ones on every
 * request anyway, and the list is never requested without technologies.
 */
export const getActivePlannedByTechnologyIds = (plannedByTechnologyIds, activeTechnologies, catalogFailed = false) => {
  const selection = getEffectivePlannedByTechnologyIds(plannedByTechnologyIds)
  if (catalogFailed || !Array.isArray(activeTechnologies)) return selection
  const activeIds = new Set(activeTechnologies.map((technology) => technology.id))
  return selection.filter((id) => activeIds.has(id))
}

/**
 * Story 193 (RN5/P3): whether the tab (or the main view) can request its list. An empty selection
 * never means "every technology": it shows a message instead.
 */
export const hasPlannedByTechnologySelection = (technologyIds) => Array.isArray(technologyIds) && technologyIds.length > 0
